import { useCallback, useMemo, useState } from 'react';

import { useApiQuery } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import { ApiResponse } from '@/shared/types/apiTypes';

import {
  CustomerFormData,
  User,
  UserListResponse,
  UserSearchParams,
} from '../types/orderTypes';

export const useUserSearch = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<CustomerFormData>({
    companyName: '',
    representativeName: '',
    businessRegistrationNumber: '',
    email: '',
    phoneNumber: '',
    customerName: '',
    customerEmail: '',
    postalCode: '',
    address: '',
  });

  const queryParams = useMemo<UserSearchParams>(
    () => ({
      page: 1,
      limit: 10,
      keyword: searchKeyword.trim(),
    }),
    [searchKeyword],
  );

  const getUserList = useCallback(
    async (
      params: UserSearchParams,
    ): Promise<ApiResponse<UserListResponse>> => {
      const urlSearchParams = new URLSearchParams({
        page: params.page?.toString() || '1',
        limit: params.limit?.toString() || '10',
      });

      if (params.keyword && params.keyword.trim()) {
        urlSearchParams.append('keyword', params.keyword);
      }

      return api.get<UserListResponse>(
        `/api/v1/user/list?${urlSearchParams.toString()}`,
      );
    },
    [],
  );

  const {
    data: userListResponse,
    isLoading,
    error,
  } = useApiQuery(
    ['userSearch', JSON.stringify(queryParams)],
    () => getUserList(queryParams),
    {
      retry: 1,
    },
  );

  const userList = userListResponse?.data?.userList || [];
  const hasResults = userList.length > 0;

  // 검색어 변경 핸들러
  const handleSearchChange = useCallback((value: string) => {
    setSearchKeyword(value);
    if (value.trim().length > 0) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  }, []);

  // 유저 선택 핸들러
  const handleUserSelect = useCallback((user: User) => {
    setSelectedUser(user);
    setSearchKeyword(user.email);
    setIsDropdownOpen(false);

    // 폼 데이터 업데이트
    setFormData({
      companyName: user.companyName,
      representativeName: user.representativeName,
      businessRegistrationNumber: user.businessRegistrationNumber,
      email: user.email,
      phoneNumber: user.phoneNumber,
      customerName: '',
      customerEmail: '',
      postalCode: '',
      address: '',
    });
  }, []);

  // 드롭다운 닫기
  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false);
  }, []);

  // 폼 필드 변경 핸들러
  const handleFormChange = useCallback(
    (field: keyof CustomerFormData, value: string) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  return {
    // Data
    userList,
    isLoading,
    error,
    hasResults,

    // State
    searchKeyword,
    isDropdownOpen,
    selectedUser,
    formData,

    // Actions
    handleSearchChange,
    handleUserSelect,
    closeDropdown,
    handleFormChange,
  };
};
